import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Link } from 'react-router';

import { TableBody, TableCell, TableHead, TableHeader, TableHeaderRow, TableRow } from 'DesignSystem/ui/table';
import { MetaProps, rightTableCellMeta } from 'DesignSystem/ui/table-meta-props';
import { cn } from 'DesignSystem/utils';
import LongText from 'components/LongText';
import { useEventItemApprovedSubstitutes } from 'pages/disruption/events/detail/hooks/use-event-item-approved-substitutes';
import type { SubstituteQueueItem } from 'pages/substitutes/types';
import { getManipulatedERPId } from 'utils/tmpDataManipulation';

import { DohCell, QohCell } from './event-items-inventory-cells';

const NESTED_TABLE_MIN_WIDTH = 'min-w-[720px]';

const descriptionTableCellMeta: MetaProps = {
  tableCellProps: {
    className: 'max-w-[230px] overflow-hidden',
    style: { maxWidth: 230 },
  },
};

const columnHelper = createColumnHelper<SubstituteQueueItem>();

const columns = [
  columnHelper.accessor('substitute_client_erp_id', {
    id: 'substitute_client_erp_id',
    header: 'ERP ID',
    cell: ({ row }) => <SubstituteErpIdCell item={row.original} />,
  }),
  columnHelper.accessor('substitute_client_item_description', {
    id: 'substitute_client_item_description',
    meta: descriptionTableCellMeta,
    header: 'Description',
    cell: ({ getValue }) => {
      const text = getValue();
      return <div className="max-w-[230px] overflow-hidden">{text ? <LongText text={text} /> : '–'}</div>;
    },
  }),
  columnHelper.accessor('substitute_client_manufacturer_name', {
    id: 'substitute_client_manufacturer_name',
    header: 'MFG Name',
    cell: ({ getValue }) => {
      const text = getValue();
      return text ? <LongText text={text} /> : '–';
    },
  }),
  columnHelper.accessor('substitute_quantity_on_hand', {
    id: 'substitute_quantity_on_hand',
    meta: rightTableCellMeta,
    header: 'QOH',
    cell: ({ getValue }) => <QohCell value={getValue()} />,
  }),
  columnHelper.accessor('substitute_days_on_hand', {
    id: 'substitute_days_on_hand',
    meta: rightTableCellMeta,
    header: 'DOH',
    cell: ({ getValue }) => <DohCell value={getValue()} />,
  }),
];

type EventItemsApprovedSubstitutesProps = {
  clientItemMasterId: string;
  erpId: string | null;
};

export default function EventItemsApprovedSubstitutes({
  clientItemMasterId,
  erpId,
}: EventItemsApprovedSubstitutesProps) {
  const flags = useFlags();
  const { items, isLoading, isError } = useEventItemApprovedSubstitutes(clientItemMasterId);
  const displayErpId = flags['demoDataManipulation'] ? getManipulatedERPId(erpId || '') : erpId;
  const table = useReactTable({
    data: items,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.substitution_request_id,
  });
  const columnCount = table.getAllColumns().length;

  return (
    <div className="bg-zinc-50 px-4 py-3">
      <p className="type-body-sm mb-3 font-medium text-zinc-900">Approved Substitutes for {displayErpId || '–'}</p>
      <div className="relative w-full overflow-visible rounded border border-zinc-200">
        <table
          data-testid="approved-substitutes-table"
          className={cn('w-full caption-bottom bg-white text-sm', NESTED_TABLE_MIN_WIDTH)}
        >
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableHeaderRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const tableCellProps = (header.column.columnDef.meta as MetaProps | undefined)?.tableCellProps ?? {};
                  const { className: cellClassName, ...restTableCellProps } = tableCellProps;
                  return (
                    <TableHead
                      key={header.id}
                      className={cn('text-nowrap', tableCellProps.align === 'right' && 'text-right', cellClassName)}
                      {...restTableCellProps}
                    >
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  );
                })}
              </TableHeaderRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={columnCount} className="h-24 text-center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : isError ? (
              <TableRow>
                <TableCell colSpan={columnCount} className="h-24 text-center">
                  Unable to load approved substitutes
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columnCount} className="h-24 text-center">
                  No approved substitutes found
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => {
                    const tableCellProps = (cell.column.columnDef.meta as MetaProps | undefined)?.tableCellProps ?? {};
                    const { className: cellClassName, ...restTableCellProps } = tableCellProps;
                    return (
                      <TableCell
                        key={cell.id}
                        className={cn(tableCellProps.align === 'right' && 'text-right', cellClassName)}
                        {...restTableCellProps}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </table>
      </div>
    </div>
  );
}

function SubstituteErpIdCell({ item }: { item: SubstituteQueueItem }) {
  const flags = useFlags();
  const erpId = flags['demoDataManipulation']
    ? getManipulatedERPId(item.substitute_client_erp_id || '')
    : item.substitute_client_erp_id;

  if (!item.substitute_client_erp_id || !item.substitute_client_item_master_id) {
    return <>{erpId || '–'}</>;
  }

  return (
    <Link
      to={`/disruptions/${item.substitute_client_item_master_id}`}
      className="font-medium text-indigo-500 hover:underline"
    >
      {erpId}
    </Link>
  );
}
